import { Controller } from "@hotwired/stimulus"
import { Toast } from "bootstrap"

export default class extends Controller {
  static values = {
    delay: { type: Number, default: 5000 }
  }

  connect() {
    // Position for stacking before showing
    this.positionToast()

    // Create and configure toast
    this.toast = new Toast(this.element, {
      autohide: true,
      delay: this.delayValue
    })

    // Show with smooth animation
    this.showWithAnimation()

    // Clean up when hidden
    this.element.addEventListener('hidden.bs.toast', () => {
      this.cleanup()
    })

    // Pause on hover (fixed)
    this.isPaused = false
    this.element.addEventListener('mouseenter', () => {
      this.pauseToast()
    })

    // Resume on mouse leave (fixed)
    this.element.addEventListener('mouseleave', () => {
      this.resumeToast()
    })
  }

  positionToast() {
    const container = document.getElementById('toast-container')
    const existingToasts = container.querySelectorAll('.toast:not(.hiding)')

    // Limit maximum toasts (remove oldest if too many)
    if (existingToasts.length >= 5) {
      const oldest = existingToasts[0]
      const oldestController = this.application.getControllerForElementAndIdentifier(oldest, 'toast')
      if (oldestController) {
        oldestController.hide()
      }
    }

    // Stack with slight offset
    if (existingToasts.length > 0) {
      const offset = existingToasts.length * 8
      this.element.style.transform = `translateY(-${offset}px)`
    }
  }

  showWithAnimation() {
    // Start with element hidden
    this.element.style.opacity = '0'
    this.element.style.transform += ' translateX(100%)'
    this.element.style.transition = 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'

    // Show the toast
    this.toast.show()

    // Animate in
    requestAnimationFrame(() => {
      this.element.style.opacity = '1'
      this.element.style.transform = this.element.style.transform.replace('translateX(100%)', 'translateX(0)')
    })
  }

  pauseToast() {
    if (!this.isPaused && this.toast) {
      this.isPaused = true
      // Store remaining time and pause
      const toastElement = this.element.querySelector('.toast')
      if (toastElement) {
        // Bootstrap doesn't provide a pause method, so we recreate without autohide
        this.toast.dispose()
        this.toast = new Toast(this.element, {
          autohide: false
        })
      }
    }
  }

  resumeToast() {
    if (this.isPaused && this.toast) {
      this.isPaused = false
      // Resume with shorter delay
      this.toast.dispose()
      this.toast = new Toast(this.element, {
        autohide: true,
        delay: 2000 // Resume with 2 seconds
      })
      // We don't call show() again as the element is already visible
    }
  }

  cleanup() {
    if (this.element.classList.contains('hiding')) return

    this.element.classList.add('hiding')

    // Simple fade and slide out
    this.element.style.transition = 'all 0.4s ease-in-out'
    this.element.style.opacity = '0'
    this.element.style.transform += ' translateX(100%)'

    // Remove element and reorder
    setTimeout(() => {
      this.element.remove()
      this.reorderToasts()
    }, 400)
  }

  reorderToasts() {
    const container = document.getElementById('toast-container')
    const remainingToasts = container.querySelectorAll('.toast:not(.hiding)')

    remainingToasts.forEach((toast, index) => {
      const offset = index * 8
      // Only update translateY, preserve other transforms
      const currentTransform = toast.style.transform || ''
      const newTransform = currentTransform.replace(/translateY\([^)]*\)/, '') + ` translateY(-${offset}px)`

      toast.style.transition = 'transform 0.3s ease-out'
      toast.style.transform = newTransform.trim()
    })
  }

  hide() {
    if (this.toast) {
      this.toast.hide()
    }
  }

  disconnect() {
    if (this.toast) {
      this.toast.dispose()
    }
  }
}